import { supabase } from '@/integrations/supabase/client';
import { Sentry } from '@/lib/sentry';

interface NotificationRecord {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: string;
  quote_id?: string;
  read: boolean;
  created_at: string;
}

/**
 * Triggers a push notification via the Edge Function.
 * This is called after a notification is created in the database.
 */
export async function triggerPushNotification(notification: NotificationRecord): Promise<{ success: boolean; error?: string; data?: any }> {
  console.log('=== PUSH DEBUG START ===');
  console.log('Notification ID:', notification.id);
  console.log('User ID:', notification.user_id);
  console.log('Title:', notification.title);
  
  try {
    const { data: responseData, error } = await supabase.functions.invoke('send-push-notification', {
      body: {
        type: 'INSERT',
        table: 'notifications',
        record: notification,
      },
    });

    console.log('Response data:', JSON.stringify(responseData));

    if (error) {
      console.error('Push notification error:', error);
      return { success: false, error: error.message, data: responseData };
    }
    
    console.log('=== PUSH DEBUG SUCCESS ===');
    return { success: true, data: responseData };
  } catch (error: any) {
    console.error('=== PUSH DEBUG FAILED ===');
    console.error('Error type:', error?.name);
    console.error('Error message:', error?.message);
    console.error('Full error:', error);
    Sentry.captureException(error, {
      extra: {
        notification_id: notification.id,
        user_id: notification.user_id,
        type: notification.type,
      },
    });
    return { success: false, error: error?.message || 'Unknown error' };
  }
}

/**
 * Fetches the latest notification for a user and triggers a push notification.
 * This is useful when we don't have access to the notification record directly.
 */
export async function triggerPushForLatestNotification(userId: string): Promise<void> {
  try {
    // Fetch the most recent notification for this user
    const { data: notification, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !notification) {
      console.error('Could not fetch latest notification:', error);
      return;
    }

    await triggerPushNotification(notification as NotificationRecord);
  } catch (error) {
    console.error('Failed to trigger push for latest notification:', error);
    Sentry.captureException(error, { extra: { user_id: userId } });
  }
}
